import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from '../utils/toast';
import { CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RenewPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [autoRenew, setAutoRenew] = useState(true);
  const [renewalSuccess, setRenewalSuccess] = useState(false);

  const handleRenew = async () => {
    setLoading(true);

    // Simulate payment processing
    setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API}/subscriptions/renew`,
          {
            billing_cycle: billingCycle,
            auto_renew: autoRenew
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        setRenewalSuccess(true);
        toast.success(response.data.message);
        
        // Redirect after 3 seconds
        setTimeout(() => {
          navigate('/subscription');
        }, 3000);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Renewal failed');
      } finally {
        setLoading(false);
      }
    }, 2000);
  };

  if (renewalSuccess) {
    return (
      <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center">
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
          <h2 className="text-[22px] font-bold mb-2" style={{ color: '#0056B3' }}>Renewal Successful!</h2>
          <p className="text-gray-600 mb-6">Your subscription has been renewed successfully.</p>
          <p className="text-sm text-gray-500">Redirecting to subscription dashboard...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Renew Subscription</h1>
            <Button onClick={() => navigate('/subscription')} variant="outline">
              Cancel
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="p-8">
          <h2 className="text-[18px] font-bold mb-6" style={{ color: '#0056B3' }}>Renewal Options</h2>

          <div className="space-y-6">
            {/* Billing Cycle Selection */}
            <div>
              <Label className="text-base font-semibold mb-3 block">Select Billing Cycle</Label>
              <div className="grid grid-cols-2 gap-4">
                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    billingCycle === 'monthly'
                      ? 'border-[#0056B3] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setBillingCycle('monthly')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Monthly</span>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      billingCycle === 'monthly' ? 'border-[#0056B3]' : 'border-gray-300'
                    }`}>
                      {billingCycle === 'monthly' && (
                        <div className="w-3 h-3 rounded-full bg-[#0056B3]"></div>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">Billed monthly</p>
                </div>

                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    billingCycle === 'yearly'
                      ? 'border-[#0056B3] bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setBillingCycle('yearly')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Yearly</span>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      billingCycle === 'yearly' ? 'border-[#0056B3]' : 'border-gray-300'
                    }`}>
                      {billingCycle === 'yearly' && (
                        <div className="w-3 h-3 rounded-full bg-[#0056B3]"></div>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">Save 10% annually</p>
                  <span className="inline-block mt-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                    Recommended
                  </span>
                </div>
              </div>
            </div>

            {/* Auto Renew */}
            <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                id="autoRenew"
                checked={autoRenew}
                onChange={(e) => setAutoRenew(e.target.checked)}
                className="mt-1 w-4 h-4 text-[#0056B3] rounded"
              />
              <div>
                <Label htmlFor="autoRenew" className="font-semibold cursor-pointer">
                  Enable Auto-Renewal
                </Label>
                <p className="text-sm text-gray-600">
                  Automatically renew your subscription before it expires to ensure uninterrupted service
                </p>
              </div>
            </div>

            {/* Information Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Your current plan will be extended by the selected billing cycle. 
                You will be charged accordingly.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4 pt-4">
              <Button
                onClick={() => navigate('/subscription')}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRenew}
                className="flex-1 bg-[#0056B3] hover:bg-[#0056B3]/90"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Processing...
                  </>
                ) : (
                  'Confirm Renewal'
                )}
              </Button>
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default RenewPage;
